import BasicControlGroup from 'components/BasicControlGroup';

import CompanyLogo from 'components/CompanyLogo';
import LinkButton from 'components/LinkButton';
import RatingStarsWidget from 'components/RatingStarsWidget';
import RatingStarsWidgetEditable from 'components/RatingStarsWidgetEditable';
import ReadMore from 'components/ReadMore';
import {ROUTE} from 'constants/route';
import {DateHelpers} from 'helpers/date-helpers';

import LinkBuilder from 'helpers/link-builder';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import NumberFormat from 'react-number-format';
import MediaQuery from 'react-responsive';
import ProfileRecommendations from '../ProfileRecommendations';

import './styles.css';

const ProfileItemPosition = props => {
  const { data, isAddReviewBtnHidden, isFirmRatingHidden } = props;

  const {
    PositionID,
    Approved,
    TitleName,
    GroupName,
    IndustryName,
    Description,
    FirmActive,
    FirmID,
    FirmName,
    FirmUrlAlias,
    FirmLogo,
    OfficeLocationName,
    CandidateRating,
    Rating,
    RatingCount,
    RoleName,
    StartMonth,
    StartYear,
    Recommendations,
    ReviewID,
    Present
  } = data;

  let {EndMonth, EndYear} = data;

  if (Present === '1') {
    const now = new Date();
    EndMonth = now.getMonth() + 1;
    EndYear = now.getFullYear();
  }

  const logoStyles = {
    width: '100px',
    height: '100px',
    fontSize: '0.8571rem',
    minWidth: '100px',
  };

  const handleEditPosition = () => {
    if (props.actions) {
      props.actions.onEditPosition(data);
    }
  };

  const handleDeletePosition = () => {
    if (props.actions) {
      props.actions.onDeletePosition(data);
    }
  };

  const handleAddRecommendation = () => {
    if (props.actions) {
      props.actions.onAddRecommendation(data);
    }
  };

  const handleBeforeSetFirmRating = () => props.actions.onBeforeSetFirmRating();

  const handleSetFirmRating = mark => {
    props.actions.onSetFirmRating(props.data, mark);
  };

  const firmLink =
    FirmUrlAlias && FirmID && FirmActive === 'Yes'
      ? LinkBuilder.getFirmLink(FirmUrlAlias)
      : null;

  const reviewLink =
    ReviewID || Approved === '0' || isAddReviewBtnHidden
      ? null
      : ROUTE.ADD_REVIEW_EMPLOYEE + `?position=${PositionID}`;

  const date = `${DateHelpers.getMonthNameByIndex(
    StartMonth - 1
  )} ${StartYear} - ${
    EndMonth && EndYear && parseInt(Present, 10) === 0
      ? `${DateHelpers.getMonthNameByIndex(EndMonth - 1)} ${EndYear}`
      : `Present`
  }`;

  const period = DateHelpers.getDiffInMonth(
    { StartMonth, StartYear },
    { EndMonth, EndYear }
  );

  const responsesText = useMemo(() => {
    let responsesText = 'No responses';
    if (Number(RatingCount) !== 0) {
      responsesText = (
        <>
          <NumberFormat
            thousandSeparator
            value={RatingCount}
            displayType={'text'}
          />{' '}
          {Number(RatingCount) > 1 ? 'responses' : 'response'}
        </>
      );
    }

    return responsesText;
  }, [RatingCount]);

  return (
    <div className="profile-item-position">
      <div className="profile-item-position__main">
        <div className="profile-item-position__avatar">
          <CompanyLogo
            name={FirmName}
            logo={FirmLogo}
            link={firmLink}
            styles={logoStyles}
          />
        </div>
        <div className="profile-item-position__content">
          <div className="profile-item-position__top">
            <h4 className="profile-item-position__title">{TitleName}</h4>
            <MediaQuery query="(min-width: 768px)">
              <div className="profile-item-position__controls">
                <BasicControlGroup>
                  {props.actions && props.actions.onEditPosition && (
                    <LinkButton text="Edit" controlFunc={handleEditPosition} />
                  )}

                  {props.actions && props.actions.onDeletePosition && (
                    <LinkButton
                      text="Delete"
                      controlFunc={handleDeletePosition}
                    />
                  )}

                  {props.actions && props.actions.onAddRecommendation && (
                    <LinkButton
                      text="Add Recommendation"
                      controlFunc={handleAddRecommendation}
                    />
                  )}
                  {reviewLink && (
                    <LinkButton text="Add Review" to={reviewLink} />
                  )}
                </BasicControlGroup>
              </div>
            </MediaQuery>
          </div>
          <div className="profile-item-position__firm">
            {firmLink ? (
              <LinkButton
                text={FirmName}
                to={firmLink}
                btnFontFamily="Noto Sans, sans-serif"
                btnFontWeight="400"
              />
            ) : (
              <span style={{ marginRight: '0.5em' }}>{FirmName}</span>
            )}
            {!isFirmRatingHidden && (
              <div className="profile-item-position__firm-rating">
                {CandidateRating ? (
                  <>
                    <RatingStarsWidget
                      value={Number(Rating)}
                      size={12}
                      showNumber={false}
                    />
                    <span className="profile-item-position__rating-text">
                      {responsesText}
                    </span>
                  </>
                ) : (
                  <RatingStarsWidgetEditable
                    value={0}
                    size={12}
                    showNumber={false}
                    onChangeHandler={handleSetFirmRating}
                    onBeforeChangeHandler={handleBeforeSetFirmRating}
                  />
                )}
              </div>
            )}
          </div>

          {(GroupName || IndustryName) && (
            <p className="profile-item-position__group-industry">
              {GroupName}
              {IndustryName && GroupName ? ` / ` : ''}
              {IndustryName}
            </p>
          )}
          <p className="profile-item-position__role">{RoleName}</p>
          <p className="profile-item-position__dates">
            <span className="profile-item-position__date">{date}</span>
            {period && (
              <span className="profile-item-position__period">{period}</span>
            )}
          </p>
          <p className="profile-item-position__location">
            {OfficeLocationName}
          </p>
        </div>
      </div>

      {Description && (
        <div className="profile-item-position__description">
          <ReadMore text={Description} readMoreCharacterLimit={180} />
        </div>
      )}

      {Recommendations && (
        <div className="profile-item-position__recommendations">
          <ProfileRecommendations list={Recommendations} />
        </div>
      )}

      <MediaQuery query="(max-width: 767px)">
        <div className="profile-item-position__controls">
          <BasicControlGroup>
            {props.actions && props.actions.onEditPosition && (
              <LinkButton text="Edit" controlFunc={handleEditPosition} />
            )}

            {props.actions && props.actions.onDeletePosition && (
              <LinkButton text="Delete" controlFunc={handleDeletePosition} />
            )}

            {props.actions && props.actions.onAddRecommendation && (
              <LinkButton
                text="Add Recommendation"
                controlFunc={handleAddRecommendation}
              />
            )}
            {reviewLink && <LinkButton text="Add Review" to={reviewLink} />}
          </BasicControlGroup>
        </div>
      </MediaQuery>
    </div>
  );
};

ProfileItemPosition.propTypes = {
  data: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  isAddReviewBtnHidden: PropTypes.bool,
  isFirmRatingHidden: PropTypes.bool,
};

export default ProfileItemPosition;
